import { keyframes, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { FeatureFlagIdBulkActionsV2, Features } from 'c1gen/features'
import { QuotaRefTenantMaxBulkActionsForOneAction } from 'c1gen/quotas'
import { C1Button, C1LoadingButton } from 'c1ui'
import { BulkActionWrapperDialogProps } from 'components/core/bulk-actions/BulkActionDialog'
import { BulkActionWrapperDrawerProps } from 'components/core/bulk-actions/BulkActionDrawer'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { useFeatureChecker } from 'components/no-ui/capability'
import { useQuota } from 'components/no-ui/context/tenant-config'
import { useDrawer } from 'hooks/useDrawer'
import { UserContext } from 'lib/context/app'
import { useSimpleModal } from 'lib/modals'
import { Status } from 'lib/rpc'
import { Check, ChevronDown, ChevronUp, Info } from 'lucide-react'
import { BulkActionState } from 'pbts/c1/api/bulkaction/v1/bulkaction'
import { FC, useState, MouseEvent, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { getBulkActionApi } from 'redux_toolkit/bulk-actions'

export interface BulkActionOption<T, E> {
    key: string
    label: React.ReactNode
    features?: Features[]
    disabled?: boolean
    behavior: {
        type: 'dialog'
        Dialog: FC<BulkActionWrapperDialogProps<T, E>>
    } | {
        type: 'drawer'
        Drawer: FC<BulkActionWrapperDrawerProps<T, E>>
    }
}

interface BulkActionsDropdownProps<T, E> {
    selections: T[]
    setSelections: (items: T[]) => void
    options: BulkActionOption<T, E>[]
    extraProps?: E
}

export const BulkActionDropdown = <T, E = object>({
    selections,
    setSelections,
    options: _options,
    extraProps,
}: BulkActionsDropdownProps<T, E>): React.ReactNode => {
    const hasBulkActionsV2 = useFeatureChecker(FeatureFlagIdBulkActionsV2)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const { trigger: triggerFetchActionStatus, data, isError } = getBulkActionApi.useLazyGetBulkAction()

    const maxBulkActions = useQuota(QuotaRefTenantMaxBulkActionsForOneAction)

    const { features, status, waitForReady } = useContext(UserContext)

    waitForReady()

    const options = _options.filter((option) => {
        if (status !== Status.OK) return false
        if (option.disabled) return false
        if (!option.features || option.features.length === 0) return true
        return option.features.every((feature) => features.has(feature))
    })

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget)
    }

    const handleClose = (): void => {
        setAnchorEl(null)
    }

    const handleDeselectClick = (): void => {
        setSelections([])
    }
    const { open: openModal } = useSimpleModal()

    const [showDrawer, close] = useDrawer()

    const handleMenuItemClick = (option: BulkActionOption<T, E>) => (): void => {
        handleClose()

        if (option.behavior.type === 'dialog') {
            openModal(option.behavior.Dialog, { setSelections, selections, triggerFetchActionStatus, extraProps })()
        } else if (option.behavior.type === 'drawer') {
            const Drawer = option.behavior.Drawer
            showDrawer(<Drawer
                setSelections={setSelections}
                selections={selections}
                triggerFetchActionStatus={triggerFetchActionStatus}
                close={close}
                extraProps={extraProps}
            />, { variant: 'sm' })
        }
    }

    const showControls = selections.length > 0 || data?.id || isError

    const actionProcessing = data?.state === BulkActionState.PENDING
        || data?.state === BulkActionState.CREATING
        || data?.state === BulkActionState.PROCESSING

    const maxSelectionsReached = selections.length > maxBulkActions

    if (!(showControls && hasBulkActionsV2 && options.length > 0)) return null

    return (
        <Stack direction="row" spacing={2} alignItems="center">
            {!(selections.length === 0 && data?.state === BulkActionState.DONE) && (
                <C1LoadingButton
                    id="actions-button"
                    aria-controls={open ? 'actions-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    endIcon={<C1Icon icon={open ? ChevronUp : ChevronDown} />}
                    disabled={selections.length === 0 || maxSelectionsReached}
                    variant="contained"
                    loading={actionProcessing}
                >
                    <FormattedMessage
                        id="items_selected_count"
                        defaultMessage="{count} selected"
                        values={{ count: selections.length }}
                    />
                </C1LoadingButton>
            )}

            <Menu
                id="actions-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{ 'aria-labelledby': 'actions-button' }}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transitionDuration={100}
            >
                {options.map((option) => <MenuItem key={option.key} onClick={handleMenuItemClick(option)}>
                    {option.label}
                </MenuItem>)}
            </Menu>

            {maxSelectionsReached && <Stack
                direction="row"
                spacing={1}
                alignItems="center"
            >
                <C1Icon icon={Info} sx={{ color: 'info.main' }} />
                <Typography>
                    <FormattedMessage
                        id="items_selected_count_maxed"
                        defaultMessage="You can select up to {max} items"
                        values={{ max: maxBulkActions }}
                    />
                </Typography>
            </Stack>}

            {selections.length > 0 && <C1Button onClick={handleDeselectClick} variant="text">
                <FormattedMessage
                    id="items_selected_deselect"
                    defaultMessage="Clear selection"
                />
            </C1Button>}

            {data?.state === BulkActionState.DONE && <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                component="div"
                sx={{ animation: `${keyframes`from { opacity: 1 } to { opacity: 0 }`} 0.5s forwards`, animationDelay: '3s' }}
                onAnimationEnd={(e: React.AnimationEvent<HTMLDivElement>): void => { (e.target as HTMLElement).style.display = 'none' }}
            >
                <C1Icon icon={Check} className="text-success-main" />
                <Typography>
                    <FormattedMessage
                        id="bulk_action_completed"
                        defaultMessage="Action complete"
                    />
                </Typography>
            </Stack>}

            {data?.state === BulkActionState.ERRORED && <MinimalAPIError
                err={<FormattedMessage
                    id="bulk_action_errored"
                    defaultMessage="There was an error performing the action"
                />}
            />}

            {isError && <MinimalAPIError
                err={<FormattedMessage
                    id="bulk_action_errored_api"
                    defaultMessage="Error fetching bulk action status"
                />}
            />}
        </Stack>
    )
}
